import { useCallback, useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useCreateGetStreamToken } from '@/queries/chat';
import { MS_IN_S } from '@/constants/date.constants';
import { StreamChannelType, type TokenResponse } from '@/api/apis/chat';

interface IJwtPayload {
  exp: number;
  iat: number;
  user_id: string;
}

const TOKEN_KEY = 'streamToken';
const GRANTS_KEY = 'streamChannelGrants';
const VALID_CHANNEL_TYPES = new Set(Object.values(StreamChannelType));

const isTokenValid = (token: string): boolean => {
  try {
    const { exp, iat } = jwtDecode<IJwtPayload>(token);
    return Date.now() < (iat + (exp - iat) * 0.75) * MS_IN_S;
  } catch {
    return false;
  }
};

const normalizeGrants = (grants?: string[]): StreamChannelType[] => {
  if (!grants?.length) return [];
  return grants.filter((g): g is StreamChannelType => VALID_CHANNEL_TYPES.has(g as StreamChannelType));
};

const loadStoredGrants = (): StreamChannelType[] | null => {
  try {
    const raw = localStorage.getItem(GRANTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as string[];
    const mapped = normalizeGrants(parsed);
    return mapped.length ? mapped : null;
  } catch {
    return null;
  }
};

export const useGetStreamToken = () => {
  const {
    mutateAsync: fetchGetStreamToken,
    isPending: isStreamTokenLoading,
    isError: isStreamTokenError,
  } = useCreateGetStreamToken();

  const [streamToken, setStreamToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [chatGrants, setChatGrants] = useState<StreamChannelType[] | null>(() => loadStoredGrants());
  const streamTokenRef = useRef(streamToken);

  const applyTokenResponse = useCallback(({ token, channelGrants }: TokenResponse) => {
    streamTokenRef.current = token;
    setStreamToken(token);
    localStorage.setItem(TOKEN_KEY, token);

    const grants = normalizeGrants(channelGrants);
    if (grants.length) {
      localStorage.setItem(GRANTS_KEY, JSON.stringify(channelGrants));
      setChatGrants(grants);
    } else {
      localStorage.removeItem(GRANTS_KEY);
      setChatGrants(null);
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || streamTokenRef.current) return;
      const tokenResponse = await fetchGetStreamToken();
      applyTokenResponse(tokenResponse);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [applyTokenResponse, fetchGetStreamToken]);

  const getValidStreamToken = useCallback(async (): Promise<string> => {
    const { current } = streamTokenRef;
    if (current && isTokenValid(current)) return current;

    const response = await fetchGetStreamToken();
    applyTokenResponse(response);
    return response.token;
  }, [applyTokenResponse, fetchGetStreamToken]);

  return {
    getValidStreamToken,
    currentStreamToken: streamToken,
    chatGrants,
    isStreamTokenLoading,
    isStreamTokenError,
  };
};
